'use client';

import { useMemo, useRef, useState } from 'react';
import { consumeToolUsage } from '@/lib/browser-tool-usage';

const RACE_DISTANCES_KM = [
  { name: '5K', km: 5 },
  { name: '10K', km: 10 },
  { name: 'Half marathon', km: 21.0975 },
  { name: 'Marathon', km: 42.195 },
];

function formatDuration(totalSeconds: number) {
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = Math.round(totalSeconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

function parseDuration(text: string): number {
  const parts = text.split(':').map((p) => Number(p) || 0);
  if (parts.length === 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length === 2) return parts[0] * 60 + parts[1];
  return parts[0] ?? 0;
}

export default function BrowserPaceCalculator() {
  const fired = useRef(false);
  const fireUsage = () => {
    if (fired.current) return;
    fired.current = true;
    void consumeToolUsage('pace-calculator', '').catch(() => undefined);
  };

  const [unit, setUnit] = useState<'mi' | 'km'>('mi');
  const [distance, setDistance] = useState(3.1);
  const [timeText, setTimeText] = useState('30:00');

  function onChange<T>(setter: (v: T) => void) {
    return (v: T) => {
      fireUsage();
      setter(v);
    };
  }

  const result = useMemo(() => {
    const totalSeconds = parseDuration(timeText);
    const paceSecondsPerUnit = distance > 0 ? totalSeconds / distance : 0;
    const km = unit === 'mi' ? distance * 1.60934 : distance;
    const secondsPerKm = km > 0 ? totalSeconds / km : 0;

    const splits = RACE_DISTANCES_KM.map((race) => ({
      name: race.name,
      time: formatDuration(secondsPerKm * race.km),
    }));

    return { paceSecondsPerUnit, splits };
  }, [distance, timeText, unit]);

  return (
    <div className="tool-live-shell">
      <div className="tool-live-header">
        <span className="tool-live-badge">Fitness</span>
        <h2 className="tool-live-title">Pace calculator</h2>
        <p className="tool-live-subtitle">
          Enter a distance and a finish time to get your pace, plus what that pace projects for
          common race distances.
        </p>
      </div>

      <div className="tool-live-grid">
        <section
          className="tool-live-uploader"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          <label className="tool-live-field">
            <span>Distance</span>
            <input
              min={0}
              onChange={(e) => onChange(setDistance)(Number(e.target.value) || 0)}
              step={0.01}
              type="number"
              value={distance}
            />
          </label>
          <label className="tool-live-field">
            <span>Unit</span>
            <select onChange={(e) => onChange(setUnit)(e.target.value as 'mi' | 'km')} value={unit}>
              <option value="mi">miles</option>
              <option value="km">kilometers</option>
            </select>
          </label>
          <label className="tool-live-field">
            <span>Time (hh:mm:ss or mm:ss)</span>
            <input
              onChange={(e) => onChange(setTimeText)(e.target.value)}
              type="text"
              value={timeText}
            />
          </label>
        </section>

        <section className="tool-live-preview">
          <p className="tool-live-panel-note">
            <strong>
              {formatDuration(result.paceSecondsPerUnit)} / {unit}
            </strong>
            <span>Your pace</span>
          </p>
          <strong style={{ display: 'block', margin: '0.4rem 0 0.1rem' }}>
            At this pace, you&apos;d finish
          </strong>
          {result.splits.map((s) => (
            <p className="tool-live-panel-note" key={s.name} style={{ padding: '0.45rem 0.75rem' }}>
              <span>
                {s.name}: <strong>{s.time}</strong>
              </span>
            </p>
          ))}
        </section>
      </div>
    </div>
  );
}
